import { useParams, Navigate } from "react-router-dom";
import { categoryPages } from "@/data/categoryPages";
import { platformPages, comparisonPages, legalPages } from "@/data/platformPages";
import { useSanityCategoryPages, useSanityPlatformPages, useSanityComparisonPages, useSanityLegalPages } from "@/hooks/useSanityContent";
import GuardedPage from "@/components/GuardedPage";
import CategoryPage from "@/pages/CategoryPage";
import PlatformPage from "@/pages/PlatformPage";
import ComparisonPage from "@/pages/ComparisonPage";
import LegalPage from "@/pages/LegalPage";

type PageKind = "category" | "platform" | "comparison" | "legal";

/**
 * Resolves a top-level /:slug to the correct page component by checking
 * all data sources (CMS first, hardcoded fallback). If no match is found,
 * renders null so React Router falls through to the * catch-all (NotFound).
 */
const DynamicSlugResolver = () => {
  const { slug } = useParams<{ slug: string }>();

  const { data: sanityCategories } = useSanityCategoryPages();
  const { data: sanityPlatforms } = useSanityPlatformPages();
  const { data: sanityComparisons } = useSanityComparisonPages();
  const { data: sanityLegal } = useSanityLegalPages();

  if (!slug) return <Navigate to="/404" replace />;

  // Determine which page type this slug belongs to.
  // CMS sources are checked first; hardcoded arrays serve as fallback.
  let kind: PageKind | null = null;

  // Category
  if (
    sanityCategories?.some((p) => p.slug?.current === slug) ||
    (sanityCategories === undefined && categoryPages.some((p) => p.slug === slug))
  ) {
    kind = "category";
  }

  // Platform
  if (
    !kind &&
    (sanityPlatforms?.some((p) => p.slug?.current === slug) ||
      (sanityPlatforms === undefined && platformPages.some((p) => p.slug === slug)))
  ) {
    kind = "platform";
  }

  // Comparison
  if (
    !kind &&
    (sanityComparisons?.some((p) => p.slug?.current === slug) ||
      (sanityComparisons === undefined && comparisonPages.some((p) => p.slug === slug)))
  ) {
    kind = "comparison";
  }

  // Legal
  if (
    !kind &&
    (sanityLegal?.some((p) => p.slug?.current === slug) ||
      legalPages.some((p) => p.slug === slug))
  ) {
    kind = "legal";
  }

  if (!kind) return <Navigate to="/404" replace />;

  const path = `/${slug}`;
  const Component =
    kind === "category"
      ? CategoryPage
      : kind === "platform"
        ? PlatformPage
        : kind === "comparison"
          ? ComparisonPage
          : LegalPage;

  return (
    <GuardedPage path={path}>
      <Component />
    </GuardedPage>
  );
};

export default DynamicSlugResolver;
